import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdAddCircle, MdFilterList } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const TripAttendance = () => {
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const [filter, setFilter] = useState({
    trip: "",
    driver: "",
    action: "FILTER",
    boarded_from: "",
    deboarde_from: ""
  });

  const navigate = useNavigate();

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const [tripDetailsMap, setTripDetailsMap] = useState({});

  const baseUrl =
    (import.meta && import.meta.env && import.meta.env.VITE_APP_BASE_URL) ||
    process.env.REACT_APP_API_BASE_URL ||
    "";
  const apiUrl = `${String(baseUrl).replace(/\/+$/, "")}/transport/attendance/read`;

  const fetchAttendance = async (opts = {}) => {
    setIsLoading(true);
    try {
      const trip_id =
        filter.trip && /^\d+$/.test(String(filter.trip).trim())
          ? parseInt(String(filter.trip).trim(), 10)
          : null;

      const payload = {
        attendance_id: null,
        student_id: null,
        student_name: null,
        school_id: userObj?.school_id || null,
        driver_id: null,
        driver_name: filter.driver?.trim() || null,
        trip_id: trip_id,
        status: null,
        date_from: null,
        date_to: null,
        boarded_from: filter.boarded_from || null,
        boarded_to: null,
        deboarded_from: filter.deboarde_from || null,
        deboarded_to: null
      };

      const { data } = await axios.post(apiUrl, payload);
      const list = Array.isArray(data) ? data : [];

      const byTrip = new Map();

      for (const r of list) {
        // Synthesize a stable group key since API doesn't return trip_id
        const key = [
          r.date || "",
          r.route_name || "",
          r.driver_id != null ? String(r.driver_id) : ""
        ].join("::") || String(r.attendance_id || Math.floor(Math.random() * 1e9));

        if (!byTrip.has(key)) {
          byTrip.set(key, {
            trip_id: key,
            // Keep your existing "trip" label used in modal header
            trip: `${r.route_name || "Route"}${r.date ? " — " + r.date : ""}`,
            date: r.date || "",
            route_name: r.route_name || "-",
            driver: r.driver_name || "-",
            studentsSet: new Set(),
            presentCount: 0,
            presentArr: [],
            absentArr: []
          });
        }

        const g = byTrip.get(key);

        const studentKey = r.student_id ?? `${r.student_name}-${g.studentsSet.size}`;
        g.studentsSet.add(studentKey);

        const isPresent = String(r.status || "").toLowerCase().startsWith("p");
        if (isPresent) {
          g.presentCount += 1;
          g.presentArr.push({
            name: r.student_name || "-",
            boarded_at: r.boarded_at || "-",
            deboarded_at: r.deboarded_at || "-"
          });
        } else {
          g.absentArr.push({
            name: r.student_name || "-",
            boarded_at: "-",
            deboarded_at: "-"
          });
        }

        if (r.driver_name) g.driver = r.driver_name;
      }

      const gridRows = Array.from(byTrip.values()).map((g) => ({
        trip_id: g.trip_id,
        trip: g.trip,
        date: g.date,
        route_name: g.route_name,
        driver: g.driver,
        students: g.studentsSet.size,
        present: g.presentCount
      }));

      const detailsMap = {};
      for (const g of byTrip.values()) {
        detailsMap[g.trip_id] = {
          present: g.presentArr,
          absent: g.absentArr
        };
      }

      setRows(gridRows);
      setTripDetailsMap(detailsMap);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || err?.message || "Failed to load attendance"
      );
      setRows([]);
      setTripDetailsMap({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openViewModal = (tripRow) => {
    setSelectedTrip(tripRow);
    setShowViewModal(true);
  };

  const closeViewModal = () => setShowViewModal(false);

  const columns = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "route_name",
      headerName: "Route Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "driver",
      headerName: "Driver",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "students",
      headerName: "Students",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "present",
      headerName: "Present",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <div className="tableActions">
          <span
            className="commonActionIcons"
            onClick={() => openViewModal(params.row)}
            title="View"
          >
            <FaEye />
          </span>
        </div>
      )
    }
  ];

  const searchableColumns = [
    (row) => row.route_name,
    (row) => row.date,
    (row) => row.driver,
    (row) => row.students,
    (row) => row.present
  ];

  const filteredRecords = (rows || []).filter((item) =>
    searchableColumns.some((selector) => {
      const value = selector(item);
      const stringValue = String(value || "")
        .toUpperCase()
        .replace(/[-\s]+/g, "");
      const normalizedQuery = searchQuery
        .toUpperCase()
        .replace(/[-\s]+/g, "");
      return stringValue.includes(normalizedQuery);
    })
  );

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Bus Attendance</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={() => setShowFilterModal(true)}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {/* <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
              <Button
                className="primaryBtn"
                variant="primary"
                onClick={() => navigate("/addtripattendance")}
              >
                <MdAddCircle />
              </Button>
            </OverlayTrigger> */}
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords.length ? filteredRecords : []}
              columns={columns}
              getRowId={(row) => row.trip_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      <Modal
        show={showViewModal}
        onHide={closeViewModal}
        centered
        size="xl"
        className="commonCenteredModal"
        dialogClassName="tripAttnWide"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>
            Trip Details {selectedTrip ? `— ${selectedTrip.trip}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ overflow: "visible" }}>
          {selectedTrip ? (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Driver:</strong> {selectedTrip.driver}
                </Col>
                <Col md={4}>
                  <strong>Total Students:</strong> {selectedTrip.students}
                </Col>
                <Col md={4}>
                  <strong>Present:</strong> {selectedTrip.present}
                </Col>
              </Row>

              <div className="commonTable small">
                <div className="tableBody">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Boarded At</th>
                        <th>Deboarded At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const details =
                          tripDetailsMap[selectedTrip.trip_id] || {};
                        const present = (details.present || []).map((s) => ({
                          ...s,
                          _status: "Present"
                        }));
                        const absent = (details.absent || []).map((s) => ({
                          ...s,
                          boarded_at: "-",
                          deboarded_at: "-",
                          _status: "Absent"
                        }));
                        const combined = [...present, ...absent];
                        return combined.map((s, idx) => (
                          <tr key={idx}>
                            <td>{s.name}</td>
                            <td>{s._status}</td>
                            <td>{s.boarded_at || "-"}</td>
                            <td>{s.deboarded_at || "-"}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div>Loading…</div>
          )}
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="secondaryBtn"
            onClick={closeViewModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="trip">
                  <Form.Label>Trip</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Trip"
                    maxLength={60}
                    value={filter.trip}
                    onChange={(e) =>
                      setFilter({ ...filter, trip: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="driver">
                  <Form.Label>Driver</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Driver"
                    maxLength={60}
                    value={filter.driver}
                    onChange={(e) =>
                      setFilter({ ...filter, driver: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={(e) => {
              e.preventDefault();
              setFilter({
                trip: "",
                driver: "",
                action: "FILTER",
                boarded_from: "",
                deboarde_from: ""
              });
            }}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={() => setShowFilterModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              onClick={() => {
                setShowFilterModal(false);
                fetchAttendance();
              }}
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TripAttendance;
